import React from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { usePlan } from '../../contexts/PlanContext';
import { getResourceLimit, canAccessFeature } from '../../config/planLimits';

/**
 * Componente informativo que mostra o status do plano do usuário
 * @param {string} feature - Nome da funcionalidade (ex: 'maxHabits', 'maxGoals', 'maxChallenges', 'maxNotes', 'maxEvents', 'maxVideos', 'maxPlaylists')
 * @param {string} featureName - Nome amigável da funcionalidade (ex: 'hábitos', 'metas', 'desafios', 'notas', 'eventos', 'vídeos', 'playlists')
 * @param {string} featureNameSingular - Nome singular da funcionalidade (ex: 'hábito', 'meta', 'desafio', 'nota', 'evento', 'vídeo', 'playlist')
 * @param {number} currentCount - Quantidade atual de itens
 * @param {object} customColors - Cores personalizadas do usuário
 * @param {boolean} showUpgradeButton - Se deve mostrar o botão de upgrade
 */
export default function PlanInfoBanner({ 
  feature, 
  featureName, 
  featureNameSingular,
  currentCount = 0, 
  customColors, 
  showUpgradeButton = true,
  onUpgradeClick
}) {
  const theme = useTheme();
  const { userPlan } = usePlan();
  
  // Debug: log para verificar os valores
  console.log('PlanInfoBanner Debug:', { feature, userPlan, currentCount });
  
  // Obtém o limite da funcionalidade
  const limit = getResourceLimit(userPlan, feature);
  
  // Debug: log para verificar o limite
  console.log('PlanInfoBanner Limit:', { feature, limit });
  
  // Verifica se a funcionalidade está disponível
  // Para recursos como maxHabits, maxGoals, etc., verificamos se o limite > 0
  const isAvailable = limit > 0;
  
  // Debug: log para verificar se está disponível
  console.log('PlanInfoBanner Available:', { feature, isAvailable });
  
  // Se a funcionalidade não estiver disponível, não mostra o banner
  if (!isAvailable) {
    console.log('PlanInfoBanner: Não disponível, retornando null');
    return null;
  }
  
  // Fallback para debug - sempre mostrar se userPlan for undefined
  if (!userPlan) {
    console.log('PlanInfoBanner: userPlan undefined, mostrando banner de debug');
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          mb: 3,
          bgcolor: 'warning.light',
          borderRadius: 2,
          border: '1px solid warning.main',
          gap: 2
        }}
      >
        <Typography variant="body2" color="warning.dark">
          DEBUG: userPlan é undefined. Feature: {feature}, Limit: {limit}
        </Typography>
      </Box>
    );
  }
  
  // Cores do banner
  const backgroundColor = customColors?.primary || theme.palette.primary.main;
  const lightBackgroundColor = `${backgroundColor}15`; // 15% de opacidade
  const textColor = customColors?.primary || theme.palette.primary.main;
  
  // Texto do banner
  let bannerText = '';
  let upgradeText = '';
  
  if (userPlan === 'PRO') {
    // Plano PRO - Ilimitado
    bannerText = `Como você tem o plano PRO, você pode criar ${featureName} ILIMITADOS!`;
    upgradeText = '';
  } else {
    // Plano BÁSICO - Limitado
    if (limit === 0) {
      // Funcionalidade não disponível
      bannerText = `Como você tem o plano Básico, ${featureName} não estão disponíveis.`;
      upgradeText = 'FAZER UPGRADE';
    } else if (limit === Infinity) {
      // Ilimitado mesmo no básico
      bannerText = `Como você tem o plano Básico, você pode criar ${featureName} ilimitados!`;
      upgradeText = '';
    } else {
      // Limitado
      const remaining = Math.max(0, limit - currentCount);
      bannerText = `Como você tem o plano Básico, você pode criar e acompanhar ${remaining}/${limit} ${featureNameSingular}${remaining !== 1 ? 's' : ''} disponíveis`;
      upgradeText = 'FAZER UPGRADE';
    }
  }
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        mb: 3,
        bgcolor: lightBackgroundColor,
        borderRadius: 2,
        border: `1px solid ${backgroundColor}30`,
        gap: 2
      }}
    >
      {/* Lado esquerdo - Ícone e texto */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: backgroundColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <InfoIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        
        <Typography
          variant="body2"
          sx={{
            color: textColor,
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.4
          }}
        >
          {bannerText}
        </Typography>
      </Box>
      
      {/* Lado direito - Botão de upgrade (se aplicável) */}
      {showUpgradeButton && upgradeText && userPlan !== 'PRO' && (
        <Button
          variant="text"
          size="small"
          sx={{
            color: textColor,
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              bgcolor: `${backgroundColor}20`,
              textDecoration: 'underline'
            }
          }}
          onClick={onUpgradeClick || (() => {
            console.log('Abrir diálogo de upgrade');
          })}
        >
          {upgradeText}
        </Button>
      )}
    </Box>
  );
}
